import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'
import type { AttendanceRecord } from '@/api/learnerProfile'
import { cn } from '@/lib/utils'
import { formatKstClockTime } from '@/utils/time'

export interface AttendanceHistoryLabels {
  present: string
  absent: string
  leave: string
  checkIn: (time: string) => string
  checkOut: (time: string) => string
  /** 하원 처리 없이 자정 자동 마감된 행의 배지. 생략하면 배지를 그리지 않는다(학습자 화면). */
  autoClosed?: string
}

/** admin 출결이력 라벨 빌더 — 모달/탭 공유. admin+common 네임스페이스 t 필요. */
export function buildAttendanceHistoryLabels(t: TFunction): AttendanceHistoryLabels {
  return {
    present: t('admin:students.attendanceHistory.status.present'),
    absent: t('admin:students.attendanceHistory.status.absent'),
    leave: t('admin:students.attendanceHistory.status.leave'),
    checkIn: (time: string) => t('admin:students.attendanceHistory.checkIn', { time }),
    checkOut: (time: string) => t('admin:students.attendanceHistory.checkOut', { time }),
    autoClosed: t('admin:students.attendanceHistory.autoClosed'),
  }
}

const AUTO_CLOSE_SOURCE = 'AUTO_CLOSE'

/** 등원·하원 시각은 서울 기준으로 찍는다 — 브라우저 로컬 시간으로 바꾸면 9시간 밀려 새벽처럼 보인다. */
function timeOf(iso: string | null): string {
  return formatKstClockTime(iso)
}

function Row({
  rec,
  labels,
  renderActions,
  renderDetails,
  className,
}: {
  rec: AttendanceRecord
  labels: AttendanceHistoryLabels
  renderActions?: (rec: AttendanceRecord) => ReactNode
  renderDetails?: (rec: AttendanceRecord) => ReactNode
  className?: string
}) {
  const isPresent = rec.status === 'PRESENT'
  const autoClosed = isPresent && rec.checkOutAt != null && rec.source === AUTO_CLOSE_SOURCE
  return (
    <div className={cn('border-b border-lc-gray-100 py-2 last:border-0', className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-lc-body-3 text-lc-gray-900">{rec.date}</span>
        <div className="flex items-center gap-2">
          <span className="text-lc-caption-1 text-lc-gray-600">
            {isPresent
              ? `${labels.present} · ${labels.checkIn(timeOf(rec.checkInAt))}${rec.checkOutAt ? ` / ${labels.checkOut(timeOf(rec.checkOutAt))}` : ''}`
              : rec.status === 'ABSENT'
                ? labels.absent
                : labels.leave}
          </span>
          {autoClosed && labels.autoClosed ? (
            <span
              className="rounded-full bg-lc-gray-100 px-2 py-0.5 text-[11px] font-semibold text-lc-gray-600"
              data-testid="attendance-auto-closed"
            >
              {labels.autoClosed}
            </span>
          ) : null}
          {renderActions ? renderActions(rec) : null}
        </div>
      </div>
      {renderDetails ? renderDetails(rec) : null}
    </div>
  )
}

export function AttendanceHistoryList({
  items,
  labels,
  renderActions,
  renderDetails,
  className,
  rowClassName,
}: {
  items: AttendanceRecord[]
  labels: AttendanceHistoryLabels
  renderActions?: (rec: AttendanceRecord) => ReactNode
  renderDetails?: (rec: AttendanceRecord) => ReactNode
  className?: string
  rowClassName?: string
}) {
  return (
    <div className={className}>
      {items.map((rec, i) => (
        <Row
          key={`${rec.attendanceId ?? rec.date}-${i}`}
          rec={rec}
          labels={labels}
          renderActions={renderActions}
          renderDetails={renderDetails}
          className={rowClassName}
        />
      ))}
    </div>
  )
}
